import { useEffect, useRef, useState } from 'react';
import { deleteRestaurantOffer, getRestaurantOffers } from '../services/offerService';
import AddOfferModal from './AddOfferModal';

const PAGE_SIZE = 20;

const editIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const trashIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const petalIcon = (
  <img
    src={`${process.env.PUBLIC_URL}/images/petals.png`}
    alt=""
    className="offer-petals-icon"
    width="14"
    height="14"
  />
);

function formatCredit(amount, currency = 'USD') {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} credit`;

  try {
    return `${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(value)} credit`;
  } catch {
    return `$${value.toFixed(2)} credit`;
  }
}

function getOfferStatus(offer) {
  if (offer.isActive === false || offer.status === 'inactive' || offer.status === 'expired') {
    return offer.status === 'expired' ? 'Expired' : 'Inactive';
  }
  return 'Active';
}

function extractOffers(response) {
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.offers)) return response.offers;
  if (Array.isArray(response)) return response;
  return [];
}

export default function RestaurantOffersModal({ open, restaurant, onClose }) {
  const [offers, setOffers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!open || !restaurant?.id) return undefined;

    let cancelled = false;
    const append = page > 1;

    async function loadOffers() {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      try {
        const response = await getRestaurantOffers(restaurant.id, {
          page,
          limit: PAGE_SIZE,
          sort: 'newest',
        });

        if (cancelled) return;

        const data = extractOffers(response);
        const totalPages = response.pagination?.totalPages || 1;
        const total =
          response.pagination?.total ??
          response.pagination?.totalItems ??
          response.pagination?.totalCount;

        setOffers((prev) => (append ? [...prev, ...data] : data));
        setHasMore(page < totalPages);
        if (typeof total === 'number') {
          setTotalCount(total);
        } else if (!append) {
          setTotalCount(data.length);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load offers.');
        if (!append) {
          setOffers([]);
          setHasMore(false);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    loadOffers();
    return () => {
      cancelled = true;
    };
  }, [open, restaurant?.id, page, reloadToken]);

  useEffect(() => {
    if (!open) {
      setShowOfferForm(false);
      setEditingOffer(null);
      setDeleteTarget(null);
      setDeleteError('');
      setDeleting(false);
      setOffers([]);
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
      setError('');
      setLoading(false);
      setLoadingMore(false);
    }
  }, [open]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!open || !sentinel || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          setPage((prev) => prev + 1);
        }
      },
      { root, rootMargin: '120px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadingMore, offers.length]);

  if (!open || !restaurant) return null;

  function refreshOffers() {
    setPage(1);
    setOffers([]);
    setHasMore(true);
    setReloadToken((token) => token + 1);
  }

  function openAddOffer() {
    setEditingOffer(null);
    setShowOfferForm(true);
  }

  function openEditOffer(offer) {
    setEditingOffer(offer);
    setShowOfferForm(true);
  }

  function closeOfferForm() {
    setShowOfferForm(false);
    setEditingOffer(null);
  }

  function openDeleteConfirm(offer) {
    setDeleteError('');
    setDeleteTarget(offer);
  }

  function closeDeleteConfirm() {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  }

  async function confirmDeleteOffer() {
    if (!deleteTarget?.id) return;

    setDeleting(true);
    setDeleteError('');

    try {
      await deleteRestaurantOffer(restaurant.id, deleteTarget.id);
      setDeleteTarget(null);
      refreshOffers();
    } catch (err) {
      if (err.status === 409) {
        setDeleteError(
          err.message ||
            'This offer cannot be deleted because coupons have already been issued. Set it to inactive instead.'
        );
      } else {
        setDeleteError(err.message || 'Failed to delete offer.');
      }
    } finally {
      setDeleting(false);
    }
  }

  const showInitialLoading = loading && offers.length === 0;
  const count = totalCount || offers.length;
  const countLabel =
    count === 1
      ? '1 offer configured for this partner venue'
      : `${count} offers configured for this partner venue`;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="offers-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="offers-modal-title"
        >
          <div className="offers-modal-header">
            <div>
              <h2 id="offers-modal-title">Offers · {restaurant.name}</h2>
              <p>{showInitialLoading ? 'Loading offers...' : countLabel}</p>
            </div>
            <button type="button" className="add-restaurant-close" onClick={onClose} aria-label="Close">
              &#x2715;
            </button>
          </div>

          <div className="offers-modal-toolbar">
            <button type="button" className="offers-add-btn" onClick={openAddOffer}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add offer
            </button>
          </div>

          {error && offers.length === 0 && <div className="add-restaurant-error">{error}</div>}

          {showInitialLoading ? (
            <div className="offers-empty">Loading offers...</div>
          ) : offers.length === 0 ? (
            <div className="offers-empty">No offers yet. Click “Add offer” to create one.</div>
          ) : (
            <div className="offers-list-scroll" ref={listRef}>
              <div className="offers-list">
                {offers.map((offer) => {
                  const status = getOfferStatus(offer);
                  const statusClass = status === 'Active' ? 'active' : 'expired';
                  const petals = offer.petalsRequired ?? offer.petals ?? 0;
                  const credit = offer.creditAmount ?? offer.credit ?? 0;
                  const currency = offer.currency || 'USD';

                  return (
                    <article key={offer.id || offer.title} className="offer-card">
                      <div className="offer-card-top">
                        <h3>{offer.title || 'Untitled offer'}</h3>
                        <span className={`offer-status-badge offer-status-${statusClass}`}>
                          <span className="offer-status-dot" />
                          {status}
                        </span>
                      </div>

                      <div className="offer-card-meta">
                        <span>
                          {petalIcon}
                          {petals} petals
                        </span>
                        <span>
                          <span className="offer-credit-icon">$</span>
                          {formatCredit(credit, currency)}
                        </span>
                      </div>

                      <div className="offer-card-actions">
                        <button type="button" className="offer-action-btn" onClick={() => openEditOffer(offer)}>
                          {editIcon}
                          Edit
                        </button>
                        <button
                          type="button"
                          className="offer-action-btn offer-action-delete"
                          onClick={() => openDeleteConfirm(offer)}
                        >
                          {trashIcon}
                          Delete
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div ref={sentinelRef} className="offers-scroll-sentinel" aria-hidden="true" />

              {loadingMore && <div className="offers-loading-more">Loading more offers...</div>}
            </div>
          )}
        </div>
      </div>

      <AddOfferModal
        open={showOfferForm}
        restaurant={restaurant}
        offer={editingOffer}
        onClose={closeOfferForm}
        onSaved={refreshOffers}
      />

      {deleteTarget && (
        <div className="modal-overlay offers-nested-overlay" onClick={closeDeleteConfirm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="modal-title">Delete Offer?</h3>
            <p className="modal-message">
              Are you sure you want to delete
              <br />
              <strong>{deleteTarget.title || 'this offer'}</strong>?
              <br />
              This action cannot be undone.
            </p>
            {deleteError && (
              <div className="add-restaurant-error" style={{ marginBottom: 16, textAlign: 'left' }}>
                {deleteError}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                onClick={closeDeleteConfirm}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-confirm"
                onClick={confirmDeleteOffer}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
